export const getCurrentPartnerId = (partnerUser) => {
  if (!partnerUser) return null;
  return (
    partnerUser.id ||
    partnerUser.partnerId ||
    (partnerUser.partner && partnerUser.partner.id) ||
    null
  );
};

export const getPartnerIdFromLocalStorage = () => {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const pid = Number(user?.customerId);
    return Number.isFinite(pid) && pid > 0 ? pid : null;
  } catch (e) {
    return null;
  }
};

// Volume pricing disabled: per-kiosk price is always the fixed monthlyPrice from JSON.

export const getKioskCountDiscountPercent = ({
  licenseKey,
  kioskCount,
  config,
}) => {
  const discounts = config?.kioskCountDiscounts || {};
  const appliesTo = Array.isArray(discounts?.appliesToLicenses)
    ? discounts.appliesToLicenses
    : null;
  if (appliesTo && !appliesTo.includes(licenseKey)) {
    return 0;
  }
  const tiers = Array.isArray(discounts?.tiers) ? discounts.tiers : [];
  const count = Number(kioskCount);
  const match =
    tiers.find((t) => {
      const min = Number(t?.min ?? 0);
      const max =
        t?.max === null || t?.max === undefined ? null : Number(t.max);
      return count >= min && (max === null || count <= max);
    }) || null;
  if (!match) return 0;
  if (match.discountPercent === null || match.discountPercent === undefined) {
    return null; // individual pricing
  }
  return Number(match.discountPercent || 0);
};

export const formatMonthlyPrice = (value, meta, t) => {
  if (value === null || value === undefined) return "—";
  const currency = meta?.currency || "EUR";
  const period =
    meta?.billingPeriod === "monthly"
      ? "month"
      : meta?.billingPeriod || "month";
  const priceStr = `${Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })} ${currency} / ${period}`;
  return meta?.pricesWithoutVat
    ? `${priceStr} • ${t("pages.dsokReports.totalWithoutVAT", {
        defaultValue: "Without VAT",
      })}`
    : priceStr;
};

export const calculateTotals = (
  { licenseType, kioskCount, commitmentOptionKey },
  config,
  partnerId
) => {
  const { licenses = {}, commitmentDiscounts = {} } = config || {};
  const lic = licenses[licenseType] || {};
  const count = Number(kioskCount);
  const kioskCountUsedForCalc =
    Number.isFinite(count) && count >= 1 ? count : 0;

  // Fixed per-kiosk price from JSON (no volume pricing)
  const customerPerKioskBase = Number(lic?.monthlyPrice || 0);
  const customerMonthlyBase = customerPerKioskBase * kioskCountUsedForCalc;

  // Kiosk count discount applicability (e.g., only basic)
  const kioskDiscountPercent = getKioskCountDiscountPercent({
    licenseKey: licenseType,
    kioskCount: kioskCountUsedForCalc,
    config,
  });
  const isIndividualPricing = kioskDiscountPercent === null;

  // commitment discounts
  const options = commitmentDiscounts?.options || {};
  const orderedKeys = ["monthly", "12m", "24m"].filter((k) => options[k]);
  const defaultKey = orderedKeys[0] || "monthly";
  const commitmentKey = commitmentOptionKey || defaultKey;
  const commitmentOption = options[commitmentKey] ||
    options[defaultKey] || { months: 1, discountPercent: 0 };
  const commitmentDiscountPercent =
    typeof commitmentOption?.discountPercent === "number"
      ? Number(commitmentOption.discountPercent)
      : 0;
  const commitmentMonths = Number(commitmentOption?.months || 1);

  // Partner unit price
  let partnerUnitPrice = null;
  if (partnerId) {
    const partners = lic?.partners || {};
    const entries = Object.values(partners || {});
    const match = entries.find((p) => Number(p?.id) === Number(partnerId));
    partnerUnitPrice = match ? Number(match.price) : null;
  }
  const partnerMonthlyBase =
    partnerUnitPrice !== null && partnerUnitPrice !== undefined
      ? partnerUnitPrice * kioskCountUsedForCalc
      : null;

  // Discounted totals (if discountPercent is a number)
  const applyKioskDiscount = (base) =>
    typeof kioskDiscountPercent === "number"
      ? base * (1 - kioskDiscountPercent / 100)
      : null;

  const customerMonthlyAfterKiosk = applyKioskDiscount(customerMonthlyBase);
  const partnerMonthlyAfterKiosk =
    partnerMonthlyBase !== null ? applyKioskDiscount(partnerMonthlyBase) : null;

  const applyCommitmentDiscount = (base) =>
    base !== null && base !== undefined
      ? base * (1 - (commitmentDiscountPercent || 0) / 100)
      : null;

  const customerMonthlyFinal = isIndividualPricing
    ? null
    : applyCommitmentDiscount(customerMonthlyAfterKiosk ?? customerMonthlyBase);
  const partnerMonthlyFinal =
    isIndividualPricing || partnerMonthlyBase === null
      ? null
      : applyCommitmentDiscount(partnerMonthlyAfterKiosk ?? partnerMonthlyBase);

  // Discount amounts (monthly)
  const customerKioskDiscountAmountMonthly = isIndividualPricing
    ? null
    : customerMonthlyBase - (customerMonthlyAfterKiosk ?? customerMonthlyBase);
  const customerCommitmentDiscountAmountMonthly = isIndividualPricing
    ? null
    : (customerMonthlyAfterKiosk ?? customerMonthlyBase) - customerMonthlyFinal;
  const customerTotalDiscountAmountMonthly = isIndividualPricing
    ? null
    : customerMonthlyBase - customerMonthlyFinal;

  const partnerKioskDiscountAmountMonthly =
    partnerMonthlyBase === null || isIndividualPricing
      ? null
      : partnerMonthlyBase - (partnerMonthlyAfterKiosk ?? partnerMonthlyBase);
  const partnerCommitmentDiscountAmountMonthly =
    partnerMonthlyBase === null || isIndividualPricing
      ? null
      : (partnerMonthlyAfterKiosk ?? partnerMonthlyBase) - partnerMonthlyFinal;
  const partnerTotalDiscountAmountMonthly =
    partnerMonthlyBase === null || isIndividualPricing
      ? null
      : partnerMonthlyBase - partnerMonthlyFinal;

  // Period totals (only for months > 1 and not individual)
  const hasPeriod = commitmentMonths > 1 && !isIndividualPricing;
  const customerPeriodBase = hasPeriod
    ? customerMonthlyBase * commitmentMonths
    : null;
  const customerPeriodFinal = hasPeriod
    ? customerMonthlyFinal * commitmentMonths
    : null;
  const customerPeriodDiscount = hasPeriod
    ? customerPeriodBase - customerPeriodFinal
    : null;

  const partnerPeriodBase =
    hasPeriod && partnerMonthlyBase !== null
      ? partnerMonthlyBase * commitmentMonths
      : null;
  const partnerPeriodFinal =
    hasPeriod && partnerMonthlyFinal !== null
      ? partnerMonthlyFinal * commitmentMonths
      : null;
  const partnerPeriodDiscount =
    hasPeriod && partnerPeriodBase !== null && partnerPeriodFinal !== null
      ? partnerPeriodBase - partnerPeriodFinal
      : null;

  return {
    kioskCountUsedForCalc,
    kioskDiscountPercent,
    isIndividualPricing,
    commitmentDiscountPercent,
    commitmentMonths,
    // per-kiosk base for customer (after volume pricing if applicable)
    customerPerKioskBase,
    // base totals
    customerMonthlyBase,
    partnerMonthlyBase,
    // after kiosk discount (may be null for individual pricing)
    customerMonthlyAfterKiosk,
    partnerMonthlyAfterKiosk,
    // final (after kiosk and commitment)
    customerMonthlyFinal,
    partnerMonthlyFinal,
    // monthly discount amounts
    customerKioskDiscountAmountMonthly,
    customerCommitmentDiscountAmountMonthly,
    customerTotalDiscountAmountMonthly,
    partnerKioskDiscountAmountMonthly,
    partnerCommitmentDiscountAmountMonthly,
    partnerTotalDiscountAmountMonthly,
    // period
    customerPeriodBase,
    customerPeriodFinal,
    customerPeriodDiscount,
    partnerPeriodBase,
    partnerPeriodFinal,
    partnerPeriodDiscount,
  };
};

export const calculateInstallationTotals = (
  { kioskCount, installation },
  config
) => {
  const fees = config?.installationFees || {};
  const travel = fees.travelCosts || {};
  const work = fees.installationWork || {};
  const setup = fees.systemSetup || {};

  const km = Number((installation?.travelKm ?? installation?.km) || 0);
  const installHours = Number(installation?.installHours || 0);

  const travelCost = km > 0 ? km * Number(travel.pricePerUnit || 0) : 0;
  const installWorkCost =
    installHours > 0 ? installHours * Number(work.pricePerUnit || 0) : 0;

  const count = Number(kioskCount);
  const hasValidCount = Number.isFinite(count) && count >= 1;
  const systemSetupCost = hasValidCount
    ? Number(setup.basePrice || 0) +
      Math.max(0, count - 1) * Number(setup.additionalUnitPrice || 0)
    : null;

  const installationTotal =
    (systemSetupCost || 0) + travelCost + installWorkCost;

  return {
    travelCost,
    installWorkCost,
    systemSetupCost,
    installationTotal,
  };
};

export const calculateGrandTotals = (
  licenseTotals,
  installationTotals,
  kioskTotals = { moc: 0, voc: 0 }
) => {
  const customerMonthly = licenseTotals?.customerMonthlyFinal ?? null;
  const partnerMonthly = licenseTotals?.partnerMonthlyFinal ?? null;
  const installOnce = Number(installationTotals?.installationTotal || 0);
  const kiosksOnce = Number(kioskTotals?.moc || 0);
  const kiosksOnceVoc = Number(kioskTotals?.voc || 0);

  const oneTimeTotal = installOnce + kiosksOnce;
  const oneTimeTotalVoc = installOnce + kiosksOnceVoc;

  const customerFirstMonth =
    customerMonthly !== null && !isNaN(oneTimeTotal)
      ? customerMonthly + oneTimeTotal
      : null;
  const partnerFirstMonth =
    partnerMonthly !== null && !isNaN(oneTimeTotalVoc)
      ? partnerMonthly + oneTimeTotalVoc
      : null;

  return {
    customerMonthly,
    partnerMonthly,
    installOnce: isNaN(installOnce) ? 0 : installOnce,
    kiosksOnce: isNaN(kiosksOnce) ? 0 : kiosksOnce,
    kiosksOnceVoc: isNaN(kiosksOnceVoc) ? 0 : kiosksOnceVoc,
    oneTimeTotal: isNaN(oneTimeTotal) ? 0 : oneTimeTotal,
    oneTimeTotalVoc: isNaN(oneTimeTotalVoc) ? 0 : oneTimeTotalVoc,
    customerFirstMonth,
    partnerFirstMonth,
  };
};

// Dynamic license options derived from config.licenses, sorted by monthlyPrice asc then name
export const getLicenseOptions = (config, partnerId = null) => {
  const licenses = (config && config.licenses) || {};

  const visibleLicenseEntries = Object.entries(licenses).filter(([key, lic]) =>
    isLicenseVisible(lic, partnerId)
  );

  const options = visibleLicenseEntries.map(([key, lic]) => {
    const price = Number(lic?.monthlyPrice);
    return {
      key,
      name: lic?.name || key,
      monthlyPrice: Number.isFinite(price) ? price : Number.POSITIVE_INFINITY,
    };
  });
  options.sort((a, b) => {
    if (a.monthlyPrice !== b.monthlyPrice) {
      return a.monthlyPrice - b.monthlyPrice;
    }
    return String(a.name).localeCompare(String(b.name));
  });
  return options;
};

export const isLicenseVisible = (license, partnerId) => {
  // Global visibility: if license.display === false, NEVER show (for anyone)
  // if license.display === true or missing, license is globally allowed (treat missing as true)
  const globalDisplay = license.display !== false;
  if (!globalDisplay) return false;

  // If no partner is logged in, license is visible (passed global check)
  if (partnerId == null) return true;

  // If partner is logged in, check for partner-specific override
  const partners = license.partners || {};
  const partnerEntry = Object.values(partners).find(
    (partner) => Number(partner.id) === Number(partnerId)
  );

  // If no partner entry found, default to SHOW (passed global check)
  if (!partnerEntry) return true;

  // If partner entry found and display === false, HIDE the license
  if (partnerEntry.display === false) return false;

  // If partner entry found and display === true or missing, SHOW the license (passed global check)
  return true;
};

export const ensureValidLicenseSelection = (currentKey, licenseOptions) => {
  if (licenseOptions.find((opt) => opt.key === currentKey)) {
    return currentKey;
  }
  return (licenseOptions[0] && licenseOptions[0].key) || currentKey;
};
